'use es6';

import { DEFAULT_STATUS } from '../constants';
import { defer, ensureFn } from './helpers';
export function adaptParametersFromApi(parameters = []) {
  return parameters.reduce((acc, parameter) => {
    acc[parameter.key] = parameter.value;
    return acc;
  }, {});
}
export function adaptTreatmentsFromApi(treatments = []) {
  return treatments.reduce((acc, treatment) => {
    acc[treatment.key] = Object.assign({}, treatment, {
      parameters: adaptParametersFromApi(treatment.parameters || [])
    });
    return acc;
  }, {});
}
export function hasValidStatus(treatment = {}) {
  return !!(treatment && treatment.status && typeof treatment.status.isActive === 'boolean' && typeof treatment.status.isEnrolled === 'boolean' && typeof treatment.status.isDefaulted === 'boolean');
}
export function canExpose(treatment = {}) {
  return !!(treatment && treatment.key && treatment.identifier);
}
export function shouldExpose(treatment = {}) {
  if (!canExpose(treatment)) return false;
  if (!treatment.status) return true;
  return !treatment.status.isDefaulted;
}
export function shouldUseOption(options = [], value) {
  return value && options.indexOf(value) !== -1;
}
export function resolveParameter(options = [], values = []) {
  return values.find(({
    value
  }) => shouldUseOption(options, value)) || values[values.length - 1];
}
export function resolveParameters(treatmentKey, treatmentParameters = {}, treatmentStatus = {}, parametersFromSchema = {}, overridesFromCache = {}, overridesFromSearch = {}) {
  let statusOverride = treatmentStatus;
  const resolvedParameters = Object.keys(parametersFromSchema).reduce((acc, paramKey) => {
    const cacheKey = `${treatmentKey}-${paramKey}`;
    const overrideFromSearch = overridesFromSearch[cacheKey];
    const overrideFromCache = overridesFromCache[cacheKey];
    const valueFromNetwork = treatmentParameters[paramKey];
    const valuesFromSchema = parametersFromSchema[paramKey];
    const values = [{
      value: overrideFromSearch,
      status: {
        isOverridden: true,
        isEnrolled: true
      }
    }, {
      value: overrideFromCache,
      status: {
        isCached: true,
        isDefaulted: false
      }
    }, {
      value: valueFromNetwork,
      status: {}
    }, {
      value: valuesFromSchema[0],
      status: {}
    }];
    const {
      value,
      status
    } = resolveParameter(valuesFromSchema, values);
    statusOverride = Object.assign({}, statusOverride, {}, status);
    return Object.assign({}, acc, {
      [paramKey]: value
    });
  }, treatmentParameters);
  return {
    resolvedParameters,
    statusOverride
  };
}
export function resolveTreatment(treatment, definition = {}, overridesFromSearch = {}, overridesFromCache = {}) {
  const treatmentParameters = treatment ? treatment.parameters || {} : {};
  const treatmentStatus = hasValidStatus(treatment) ? treatment.status : DEFAULT_STATUS;
  const resolved = Object.assign({}, treatment || definition);
  const {
    resolvedParameters,
    statusOverride
  } = resolveParameters(definition.key, treatmentParameters, treatmentStatus, definition.parameters, overridesFromCache, overridesFromSearch);
  resolved.parameters = resolvedParameters;
  resolved.status = statusOverride;

  if (typeof resolved.status.isCached === 'undefined') {
    resolved.status.isCached = false;
  }

  if (typeof definition.maxAge === 'number') {
    resolved.maxAge = definition.maxAge;
  }

  return resolved;
}
export function resolveTreatments({
  treatments = [],
  schema = {},
  overridesFromCache = {},
  overridesFromSearch = {}
} = {}) {
  const adaptedTreatments = adaptTreatmentsFromApi(treatments);
  return Object.keys(schema).reduce((acc, key) => {
    const definition = schema[key];
    definition.key = key;
    const treatment = adaptedTreatments[key];
    const resolvedTreatment = resolveTreatment(treatment, definition, overridesFromSearch, overridesFromCache);
    return Object.assign({}, acc, {
      [key]: resolvedTreatment
    });
  }, adaptedTreatments);
}
export function resolveExposure(treatmentOrKey, treatments = {}) {
  const keyMode = typeof treatmentOrKey === 'string';
  const treatment = keyMode ? treatments[treatmentOrKey] : treatmentOrKey;

  if (shouldExpose(treatment)) {
    return {
      key: treatment.key,
      identifier: treatment.identifier
    };
  }

  return null;
}
export function resolveExposures({
  treatments = {},
  treatmentOrKeys = []
} = {}) {
  return treatmentOrKeys.map(treatmentOrKey => resolveExposure(treatmentOrKey, treatments)).filter(exposure => !!exposure);
}
export function createDeferredCallstack(fnHead) {
  let callstack = fnHead;

  const deferExecution = data => {
    defer(() => {
      if (typeof callstack === 'function') {
        const ref = callstack;
        callstack = undefined;
        ref(data);
      }
    });
  };

  return {
    execute: deferExecution,
    defer: fnTail => {
      const ref = callstack;

      callstack = data => {
        ensureFn(ref)(data);
        fnTail(data);
      };
    }
  };
}
export function createAsyncResolver({
  getAsyncData,
  resolve,
  onError
} = {}) {
  let cache;
  let callstack;
  return callback => {
    if (callstack) {
      callstack.defer(callback);

      if (cache) {
        callstack.execute(resolve(cache));
      }
    } else {
      callstack = createDeferredCallstack(callback);
      getAsyncData({
        onComplete: data => {
          cache = data || {};
          callstack.execute(resolve(cache));
        },
        onError: (err, data) => {
          ensureFn(onError)(err, {
            fingerprint: ['laboratory-lib', 'lab:getAsyncData'],
            extra: {
              data
            }
          });
          cache = data;
          callstack.execute(resolve(cache));
        }
      });
    }
  };
}
export function deserializeCopyVariant(serialized) {
  const parts = serialized.split(':::');
  const key = parts[1];
  const variant = parts[2].split('__')[0];
  return [key, variant];
}
export function isCopyVariant(key) {
  return key.indexOf('__copym:::') !== -1;
}
export function isSwappableCopyVariant(key, treatments) {
  const [treatmentKey, variant] = deserializeCopyVariant(key);
  const treatment = treatments[treatmentKey];
  return treatment && treatment.parameters && treatment.parameters['group'] === variant;
}
export function findSwappableCopy({
  translations = {},
  treatments = {},
  onError
} = {}) {
  try {
    const allTranslations = [translations, translations['translate-copy'], translations['do-not-translate-copy']];
    const flatTranslations = allTranslations.filter(branch => !!branch).reduce((acc, branch) => Object.assign({}, acc, {}, branch), {});
    const copyVariants = Object.keys(flatTranslations).filter(isCopyVariant);
    return copyVariants.reduce((acc, key) => {
      if (isSwappableCopyVariant(key, treatments)) {
        acc[key] = flatTranslations[key];
      }

      return acc;
    }, {});
  } catch (err) {
    ensureFn(onError)(err);
    return {};
  }
}
export function verifyIfNetworkRequestIsNeeded({
  ceid = '',
  cacheKeys = [],
  schema = {},
  overridesFromCache = {}
} = {}) {
  // Note.: `ceid` stands for the `copy experiments identifier` this is a unique identifier for the copy experiments
  // And needed for now in order to make copy experiments work. See: https://git.hubteam.com/HubSpot/Hublytics/issues/2706
  const isRequestingCopyExperimentData = !!ceid; // Currently the `treatments` object "allows" the `parameters` object to be empty even thought this is an invalid schema
  // To respect the current use cases and not break Applications, we will be supporting this until we remove the `treatments` schema completely
  // @deprecated should be removed once the treatments schema is removed

  const isSchemaNotEmptyButParametersAre = !cacheKeys.length && !!Object.keys(schema).length; // `cacheKeys` is an object created from the `treatments` coming from your `experiments.yaml` or `treatments` object passed when
  // creating the laboratory-lib client with `createClient`
  // This tests if either the treatments schema is empty or if there's any treatment key that doesn't exist within the cache

  const cacheHasMissingTreatmentKeys = cacheKeys.length && cacheKeys.some(({
    key
  }) => !overridesFromCache[key.trim()]);
  return isRequestingCopyExperimentData || isSchemaNotEmptyButParametersAre || cacheHasMissingTreatmentKeys;
}